import React from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Stack, Typography } from '@mui/material';
import Colors from '../../../../library/Colors';
import { useAnchor } from '../../search/WebshopConfigSearch';

/**
 * Lightweight wrapper used by every per-section config card. Centralises the
 * Paper styling, title+description header and inner spacing so each card file
 * only has to focus on its own fields. Pass `sx` to override per-card.
 *
 * `headerActions` renders inline on the right of the title — handy for
 * search boxes / "Add new" buttons that would otherwise eat a full row.
 *
 * `anchor` registers this card with the easter-egg search system. When a
 * search hit dispatches the matching anchor id, the card scrolls into view
 * and briefly flashes a blue ring to draw the eye.
 */
const ConfigCard = ({ anchor, title, description, headerActions, theme = 'light', children, sx }) => {
    const isDark = theme === 'dark';
    const cardBg     = isDark ? '#2d333b' : '#ffffff';
    const borderCol  = isDark ? '#3a3f47' : '#e4e7eb';
    const textColor  = isDark ? Colors.dark_text : Colors.light_text;
    const mutedText  = isDark ? '#b0b8c1' : '#768390';

    const flash = useAnchor(anchor);

    return (
        <Paper id={anchor || undefined} elevation={0} sx={{
            p: 2.5,
            borderRadius: 2,
            border: `1px solid ${borderCol}`,
            backgroundColor: cardBg,
            // Subtle resting depth + a gentle lift on hover (shadow only, no
            // transform — these are containers, not buttons) for a more refined,
            // tactile feel. Light/dark tuned separately.
            boxShadow: isDark ? '0 1px 2px rgba(0,0,0,0.30)' : '0 1px 2px rgba(16,24,40,0.05)',
            transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
            '&:hover': {
                boxShadow: isDark ? '0 6px 20px rgba(0,0,0,0.45)' : '0 6px 20px rgba(16,24,40,0.10)',
                borderColor: isDark ? '#4a5159' : '#d0d5dd',
            },
            // Flash: a soft blue ring (light theme) / amber ring (dark theme)
            // for ~1.5s. Border colour shifts in sync so the card feels lit up
            // rather than just outlined.
            ...(flash && {
                boxShadow: isDark
                    ? '0 0 0 3px rgba(245, 184, 75, 0.55)'
                    : `0 0 0 3px ${Colors.sft_blue}66`,
                borderColor: isDark ? 'rgba(245, 184, 75, 0.9)' : Colors.sft_blue,
            }),
            ...sx,
        }}>
            <Box sx={{
                mb: 1.5,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: borderCol,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: textColor, lineHeight: 1.2 }}>
                        {title}
                    </Typography>
                    {description && (
                        <Typography variant="caption" sx={{ color: mutedText, display: 'block', mt: 0.5 }}>
                            {description}
                        </Typography>
                    )}
                </Box>
                {headerActions && (
                    <Box sx={{ flexShrink: 0 }}>{headerActions}</Box>
                )}
            </Box>
            <Stack spacing={2}>
                {children}
            </Stack>
        </Paper>
    );
};

ConfigCard.propTypes = {
    anchor:        PropTypes.string,
    title:         PropTypes.node.isRequired,
    description:   PropTypes.node,
    headerActions: PropTypes.node,
    theme:         PropTypes.string,
    children:      PropTypes.node,
    sx:            PropTypes.object,
};

export default ConfigCard;
